import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useTicket, useTicketEvents } from '@/hooks/useTickets';
import { DashboardLayout } from '@/components/shared/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { Timeline } from '@/components/shared/Timeline';
import { LoadingSpinner } from '@/components/shared/LoadingStates';
import IssueDescription from '@/components/shared/IssueDescription';
import { ArrowLeft, Phone, MapPin, Package, ShieldCheck, User, Wrench, CalendarBlank, Lock, Star } from '@phosphor-icons/react';
import { formatDateTime } from '@/lib/utils';
import useAppStore from '@/stores/appStore';
import { toast } from 'sonner';
import { ticketsApi } from '@/lib/api';

const CLOSED_STATUSES = ['closed', 'completed', 'rejected'];

// Progress stepper for customer
const TicketProgress = ({ status }) => {
  const steps = [
    { key: 'submitted', label: 'Submitted' },
    { key: 'reviewed', label: 'Reviewed' },
    { key: 'assigned', label: 'Tech Assigned' },
    { key: 'in_progress', label: 'In Progress' },
    { key: 'completed', label: 'Completed' },
  ];

  const getStepIndex = () => {
    switch (status) {
      case 'submitted': return 0;
      case 'picked_up': return 1;
      case 'approved': return 1;
      case 'assigned': return 2;
      case 'in_progress': return 3;
      case 'completed': return 4;
      case 'closed': return 5;
      default: return 0;
    }
  };

  const activeStep = getStepIndex();

  return (
    <div className="flex items-center w-full">
      {steps.map((step, idx) => {
        const isCompleted = idx < activeStep;
        const isActive = idx === activeStep;
        return (
          <div key={step.key} className="flex items-center flex-1 last:flex-none">
            <div className="flex flex-col items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-all ${
                isCompleted
                  ? 'bg-emerald-500 border-emerald-500 text-white'
                  : isActive
                    ? 'bg-blue-500 border-blue-500 text-white animate-pulse'
                    : 'bg-white border-slate-200 text-slate-400'
              }`}>
                {isCompleted ? '✓' : idx + 1}
              </div>
              <span className={`text-[10px] mt-1 font-semibold whitespace-nowrap ${
                isCompleted ? 'text-emerald-600' : isActive ? 'text-blue-600' : 'text-slate-400'
              }`}>
                {step.label}
              </span>
            </div>
            {idx < steps.length - 1 && (
              <div className={`flex-1 h-0.5 mx-1 mt-[-16px] ${
                idx < activeStep ? 'bg-emerald-400' : 'bg-slate-200'
              }`} />
            )}
          </div>
        );
      })}
    </div>
  );
};

export const TicketDetail = () => {
  const { id } = useParams();
  const { data: ticket, isLoading } = useTicket(id);
  const { data: events } = useTicketEvents(id);
  const tenantConfig = useAppStore((state) => state.tenantConfig);
  const brandColor = tenantConfig?.brand_colour || '#002FA7';

  const queryClient = useQueryClient();
  const [techStars, setTechStars] = useState(0);
  const [hoverTechStars, setHoverTechStars] = useState(0);
  const [techComment, setTechComment] = useState('');

  const [agentStars, setAgentStars] = useState(0);
  const [hoverAgentStars, setHoverAgentStars] = useState(0);
  const [agentComment, setAgentComment] = useState('');
  const [submittingRating, setSubmittingRating] = useState(false);

  if (isLoading) return <LoadingSpinner fullScreen />;
  if (!ticket) return <div>Ticket not found</div>;

  const isClosed = CLOSED_STATUSES.includes(ticket.status);
  const hasOtps = ticket.start_otp || ticket.close_otp;
  const showOtps = !isClosed && hasOtps;

  const handleSubmitRating = async () => {
    const payload = {};
    if (ticket.tech_name) {
      if (techStars === 0) {
        toast.error('Please select a star rating for the technician');
        return;
      }
      payload.tech_stars = techStars;
      payload.tech_comment = techComment;
    }
    if (ticket.agent_name) {
      if (agentStars === 0) {
        toast.error('Please select a star rating for the support agent');
        return;
      }
      payload.agent_stars = agentStars;
      payload.agent_comment = agentComment;
    }

    setSubmittingRating(true);
    try {
      await ticketsApi.rate(id, payload);
      toast.success('Thank you for your rating and feedback!');
      queryClient.invalidateQueries({ queryKey: ['ticket', id] });
      queryClient.invalidateQueries({ queryKey: ['ticketEvents', id] });
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || 'Failed to submit rating');
    } finally {
      setSubmittingRating(false);
    }
  };

  return (
    <DashboardLayout role="customer">
      <div className="p-4 sm:p-6 lg:p-8 max-w-5xl mx-auto animate-fade-in">
        {/* Back nav */}
        <Link to="/customer/tickets">
          <Button variant="ghost" className="mb-4 text-slate-500 hover:text-slate-800" data-testid="back-to-dashboard-btn">
            <ArrowLeft size={18} className="mr-2" />
            Back to Tickets
          </Button>
        </Link>

        {/* Ticket Header Card */}
        <Card className="p-5 sm:p-6 mb-6 border-0 shadow-sm" style={{ background: `linear-gradient(135deg, ${brandColor}08, ${brandColor}04)` }}>
          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-5">
            <div>
              <div className="flex items-center gap-3 mb-1">
                <h1 className="text-2xl sm:text-3xl font-black tracking-tight text-slate-900">
                  {ticket.ticket_number}
                </h1>
                <StatusBadge status={ticket.status} />
              </div>
              <p className="text-sm text-slate-500">
                <CalendarBlank size={14} className="inline mr-1" />
                Created {formatDateTime(ticket.created_at)}
              </p>
            </div>
          </div>

          {/* Progress Stepper */}
          <div className="bg-white rounded-xl p-4 border border-slate-100">
            <TicketProgress status={ticket.status} />
          </div>
        </Card>

        {(() => {
          const hasSidebarContent = !!ticket.tech_name || !!ticket.service_address;
          return (
            <div className={`grid grid-cols-1 ${hasSidebarContent ? 'lg:grid-cols-3' : 'lg:grid-cols-1'} gap-6`}>
              {/* Left Column */}
              <div className={`${hasSidebarContent ? 'lg:col-span-2' : ''} space-y-5`}>

            {/* OTP Cards — only when active and OTPs exist */}
            {showOtps && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {ticket.start_otp && (
                  <Card className="p-5 border-emerald-200 bg-gradient-to-br from-emerald-50 to-white" data-testid="start-otp-card">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="p-1.5 bg-emerald-100 rounded-lg">
                        <ShieldCheck size={16} className="text-emerald-600" weight="fill" />
                      </div>
                      <h3 className="font-bold text-xs uppercase tracking-wider text-emerald-800">Start OTP</h3>
                    </div>
                    <p className="text-3xl font-black tracking-[0.2em] text-emerald-900 font-mono select-all mb-1" data-testid="start-otp">
                      {ticket.start_otp}
                    </p>
                    <p className="text-[10px] text-emerald-600 leading-relaxed">
                      Share with technician when they arrive at your location
                    </p>
                  </Card>
                )}
                {ticket.close_otp && (
                  <Card className="p-5 border-amber-200 bg-gradient-to-br from-amber-50 to-white" data-testid="close-otp-card">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="p-1.5 bg-amber-100 rounded-lg">
                        <Lock size={16} className="text-amber-600" weight="fill" />
                      </div>
                      <h3 className="font-bold text-xs uppercase tracking-wider text-amber-800">Close OTP</h3>
                    </div>
                    <p className="text-3xl font-black tracking-[0.2em] text-amber-900 font-mono select-all mb-1" data-testid="close-otp">
                      {ticket.close_otp}
                    </p>
                    <p className="text-[10px] text-amber-600 leading-relaxed">
                      Share only when the job is done and you are satisfied
                    </p>
                  </Card>
                )}
              </div>
            )}

            {/* Rating Section */}
            {(ticket.status === 'completed' || ticket.status === 'closed') && (ticket.tech_name || ticket.agent_name) && (
              <Card className="p-5 border border-slate-100 bg-white shadow-sm space-y-5">
                <div className="flex items-center gap-2 pb-2 border-b">
                  <div className="p-1.5 bg-amber-50 rounded-lg">
                    <Star size={18} className="text-amber-500" weight="fill" />
                  </div>
                  <div>
                    <h3 className="font-bold text-sm text-slate-800">Rate Your Service & Support</h3>
                    <p className="text-[10px] text-muted-foreground">Share your feedback to help us improve</p>
                  </div>
                </div>

                {ticket.is_rated ? (
                  <div className="space-y-4">
                    {ticket.agent_name && (
                      <div className="bg-slate-50 p-4 rounded-xl space-y-2 border border-slate-100">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-bold text-slate-700">Support Agent: {ticket.agent_name}</span>
                          <div className="flex gap-0.5">
                            {[1, 2, 3, 4, 5].map((s) => (
                              <Star 
                                key={s} 
                                size={14} 
                                className={s <= (ticket.agent_stars || 0) ? 'text-amber-500' : 'text-slate-200'} 
                                weight="fill" 
                              />
                            ))}
                          </div>
                        </div>
                        {ticket.agent_comment && ticket.agent_comment !== 'None' && (
                          <div>
                            <span className="text-[9px] uppercase font-bold text-slate-400 block mb-0.5">Comment</span>
                            <p className="text-xs text-slate-700 italic">"{ticket.agent_comment}"</p>
                          </div>
                        )}
                      </div>
                    )}

                    {ticket.tech_name && (
                      <div className="bg-slate-50 p-4 rounded-xl space-y-2 border border-slate-100">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-bold text-slate-700">Technician: {ticket.tech_name}</span>
                          <div className="flex gap-0.5">
                            {[1, 2, 3, 4, 5].map((s) => (
                              <Star 
                                key={s} 
                                size={14} 
                                className={s <= (ticket.tech_stars || 0) ? 'text-amber-500' : 'text-slate-200'} 
                                weight="fill" 
                              />
                            ))}
                          </div>
                        </div>
                        {ticket.tech_comment && ticket.tech_comment !== 'None' && (
                          <div>
                            <span className="text-[9px] uppercase font-bold text-slate-400 block mb-0.5">Comment</span>
                            <p className="text-xs text-slate-700 italic">"{ticket.tech_comment}"</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-6">
                    {ticket.agent_name && (
                      <div className="space-y-3 p-4 bg-slate-50/50 rounded-xl border border-slate-100/80">
                        <h4 className="font-bold text-xs text-slate-800">Rate Support Agent: <span className="text-blue-600">{ticket.agent_name}</span></h4>
                        <div className="flex items-center gap-3">
                          <span className="text-xs font-semibold text-slate-600">Rating *</span>
                          <div className="flex gap-1">
                            {[1, 2, 3, 4, 5].map((s) => (
                              <button
                                key={s}
                                type="button"
                                onClick={() => setAgentStars(s)}
                                onMouseEnter={() => setHoverAgentStars(s)}
                                onMouseLeave={() => setHoverAgentStars(0)}
                                className="transition-transform active:scale-90"
                              >
                                <Star 
                                  size={22} 
                                  className={(hoverAgentStars || agentStars) >= s ? 'text-amber-400' : 'text-slate-200'} 
                                  weight="fill" 
                                />
                              </button>
                            ))}
                          </div>
                        </div>

                        <div className="space-y-1.5">
                          <label htmlFor="agent-comment" className="text-[9px] font-bold uppercase tracking-wider text-slate-500">
                            Agent Feedback (Optional)
                          </label>
                          <textarea
                            id="agent-comment"
                            placeholder="How did the agent assist you today?"
                            value={agentComment}
                            onChange={(e) => setAgentComment(e.target.value)}
                            rows={2}
                            className="w-full text-xs p-2.5 rounded-lg border border-slate-200 bg-white focus:outline-none focus:ring-1 focus:ring-primary resize-none transition-colors text-slate-800"
                          />
                        </div>
                      </div>
                    )}

                    {ticket.tech_name && (
                      <div className="space-y-3 p-4 bg-slate-50/50 rounded-xl border border-slate-100/80">
                        <h4 className="font-bold text-xs text-slate-800">Rate Technician: <span className="text-blue-600">{ticket.tech_name}</span></h4>
                        <div className="flex items-center gap-3">
                          <span className="text-xs font-semibold text-slate-600">Rating *</span>
                          <div className="flex gap-1">
                            {[1, 2, 3, 4, 5].map((s) => (
                              <button
                                key={s}
                                type="button"
                                onClick={() => setTechStars(s)}
                                onMouseEnter={() => setHoverTechStars(s)}
                                onMouseLeave={() => setHoverTechStars(0)}
                                className="transition-transform active:scale-90"
                              >
                                <Star 
                                  size={22} 
                                  className={(hoverTechStars || techStars) >= s ? 'text-amber-400' : 'text-slate-200'} 
                                  weight="fill" 
                                />
                              </button>
                            ))}
                          </div>
                        </div>

                        <div className="space-y-1.5">
                          <label htmlFor="tech-comment" className="text-[9px] font-bold uppercase tracking-wider text-slate-500">
                            Technician Feedback (Optional)
                          </label>
                          <textarea
                            id="tech-comment"
                            placeholder="How was the technician's service?"
                            value={techComment}
                            onChange={(e) => setTechComment(e.target.value)}
                            rows={2}
                            className="w-full text-xs p-2.5 rounded-lg border border-slate-200 bg-white focus:outline-none focus:ring-1 focus:ring-primary resize-none transition-colors text-slate-800"
                          />
                        </div>
                      </div>
                    )}

                    <Button
                      type="button"
                      disabled={
                        submittingRating || 
                        (ticket.tech_name && techStars === 0) || 
                        (ticket.agent_name && agentStars === 0)
                      }
                      onClick={handleSubmitRating}
                      className="w-full font-bold text-xs uppercase tracking-wider h-10 text-white"
                      style={{ backgroundColor: brandColor }}
                    >
                      {submittingRating ? 'Submitting...' : 'Submit Feedback'}
                    </Button>
                  </div>
                )}
              </Card>
            )}

            {/* Product & Issue Details */}
            <Card className="p-5 space-y-4">
              <h2 className="font-bold text-sm uppercase tracking-wider text-slate-400">Product & Issue</h2>

              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-xl border border-slate-100">
                <div className="p-2 bg-white rounded-lg border border-slate-200 shrink-0">
                  <Package size={20} className="text-slate-500" />
                </div>
                <div className="min-w-0">
                  <p className="font-bold text-slate-900">{ticket.product_name}</p>
                  <p className="text-xs text-slate-500 font-mono mt-0.5">Serial: {ticket.serial_number}</p>
                </div>
              </div>

              {ticket.issue_category && (
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1.5">Issue Category</p>
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-red-50 border border-red-200 rounded-lg">
                    <span className="w-2 h-2 rounded-full bg-red-400" />
                    <span className="font-bold text-sm text-red-800">{ticket.issue_category}</span>
                  </div>
                </div>
              )}

              <IssueDescription description={ticket.description} />
            </Card>

            {/* Activity Timeline */}
            <Card className="p-5" data-testid="timeline-card">
              <h2 className="font-bold text-sm uppercase tracking-wider text-slate-400 mb-4">Activity Timeline</h2>
              <Timeline events={events} customerView={true} />
            </Card>
          </div>

          {/* Right Sidebar */}
          {hasSidebarContent && (
            <div className="space-y-5">
              {/* Technician Info */}
              {ticket.tech_name && (
                <Card className="p-5 border-0 shadow-sm bg-gradient-to-br from-violet-50/50 to-white" data-testid="technician-card">
                  <h3 className="font-bold text-xs uppercase tracking-wider text-slate-400 mb-3 flex items-center gap-1.5">
                    <Wrench size={12} /> Assigned Technician
                  </h3>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full bg-violet-100 flex items-center justify-center">
                      <User size={20} className="text-violet-600" weight="fill" />
                    </div>
                    <div>
                      <p className="font-bold text-sm text-slate-900">{ticket.tech_name}</p>
                      {ticket.tech_rating && (
                        <p className="text-xs text-amber-500 font-bold">
                          {'⭐'.repeat(Math.round(ticket.tech_rating))} {ticket.tech_rating.toFixed(1)}
                        </p>
                      )}
                    </div>
                  </div>
                  {ticket.tech_phone && (
                    <a
                      href={`tel:${ticket.tech_phone}`}
                      className="flex items-center justify-center gap-2 w-full py-2.5 rounded-lg text-sm font-bold transition-colors"
                      style={{ backgroundColor: `${brandColor}10`, color: brandColor }}
                      data-testid="tech-phone-link"
                    >
                      <Phone size={16} weight="fill" />
                      Call Technician
                    </a>
                  )}
                </Card>
              )}

              {/* Service Location */}
              {ticket.service_address && (
                <Card className="p-5" data-testid="customer-info-card">
                  <h3 className="font-bold text-xs uppercase tracking-wider text-slate-400 mb-3 flex items-center gap-1.5">
                    <MapPin size={12} /> Service Location
                  </h3>
                  <div className="flex items-start gap-2 p-3 bg-slate-50 rounded-lg border border-slate-100 mb-3">
                    <MapPin size={16} className="text-slate-400 mt-0.5 shrink-0" />
                    <p className="text-sm text-slate-700 leading-relaxed">{ticket.service_address}</p>
                  </div>
                  {ticket.customer_lat && ticket.customer_lng && (
                    <div className="pt-3 border-t border-slate-100 flex items-center justify-between flex-wrap gap-2">
                      <span className="text-xs font-mono font-medium text-slate-400">
                        GPS: {ticket.customer_lat}, {ticket.customer_lng}
                      </span>
                      <a 
                        href={`https://www.google.com/maps/search/?api=1&query=${ticket.customer_lat},${ticket.customer_lng}`}
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs font-bold text-primary hover:underline"
                        data-testid="customer-ticket-gmap-link"
                      >
                        <MapPin size={14} weight="bold" /> View on Google Maps
                      </a>
                    </div>
                  )}
                </Card>
              )}

            </div>
          )}
        </div>
      );
    })()}
      </div>
    </DashboardLayout>
  );
};